import React, { useState } from 'react';
import { Box, Paper, Typography, Avatar, Chip, IconButton } from '@mui/material';
import {  Person, SmartToy, Mic, PlayArrow, Pause } from '@mui/icons-material';
import { Message } from '@/types/chat';
import { useSettings } from '@/context/SettingsContext';
import InteractiveComponents from './InteractiveComponents';
import { format } from 'date-fns';
import { audioService } from '@/services/audioService';

interface MessageListProps {
  messages: Message[];
  onInteractionResponse: (value: any) => void;
}

const MessageList: React.FC<MessageListProps> = ({ messages, onInteractionResponse }) => {
  const { language } = useSettings();
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  const handlePlayVoice = async (messageId: string, audioBlob: Blob) => {
    if (playingMessageId === messageId) {
      if (isPaused) {
        // Resume playback
        audioService.resume();
        setIsPaused(false);
      } else {
        // Pause playback
        audioService.pause();
        setIsPaused(true);
      }
    } else {
      // Stop any current audio and play this one
      audioService.stop();
      setPlayingMessageId(messageId);
      setIsPaused(false);
      await audioService.playAudio(audioBlob, () => {
        setPlayingMessageId(null);
        setIsPaused(false);
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {messages.map((message) => (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
            gap: 1,
            alignItems: 'flex-start',
          }}
        >
          {/* Avatar */}
          <Avatar
            sx={{
              bgcolor: message.sender === 'user' ? 'primary.main' : 'secondary.main',
            }}
          >
            {message.sender === 'user' ? <Person /> : <SmartToy />}
          </Avatar>

          {/* Message Content */}
          <Box sx={{ maxWidth: '70%', minWidth: '200px' }}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                bgcolor:
                  message.sender === 'user'
                    ? 'primary.light'
                    : 'background.paper',
                borderRadius: 2,
              }}
            >
              {/* Voice message with playback (for both user and agent messages) */}
              {message.type === 'voice' && message.content.audioBlob ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    color="primary"
                    onClick={() => handlePlayVoice(message.id, message.content.audioBlob!)}
                    size="small"
                  >
                    {playingMessageId === message.id
                      ? isPaused
                        ? <PlayArrow />
                        : <Pause />
                      : <PlayArrow />}
                  </IconButton>
                  <Chip
                    icon={<Mic />}
                    label={language === 'en' ? 'Voice message' : 'رسالة صوتية'}
                    size="small"
                  />
                </Box>
              ) : (
                <>
                  {/* Voice indicator for messages without blob */}
                  {message.type === 'voice' && (
                    <Chip
                      icon={<Mic />}
                      label={language === 'en' ? 'Voice' : 'صوت'}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}

                  {/* Text content */}
                  {message.content.text && (
                    <Typography
                      variant="body1"
                      sx={{
                        color:
                          message.sender === 'user'
                            ? 'primary.contrastText'
                            : 'text.primary',
                        direction: language === 'ar' ? 'rtl' : 'ltr',
                      }}
                    >
                      {message.content.text}
                    </Typography>
                  )}
                </>
              )}

              {/* Interactive component */}
              {message.content.component && (
                <Box sx={{ mt: 2 }}>
                  <InteractiveComponents
                    component={message.content.component}
                    onResponse={onInteractionResponse}
                  />
                </Box>
              )}
            </Paper>

            {/* Timestamp */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: 0.5,
                textAlign: message.sender === 'user' ? 'right' : 'left',
              }}
            >
              {format(message.timestamp, 'HH:mm')}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default MessageList;
